import { mapChildren, pathText } from './ast.js';
import { FormulaErrorCodes, formulaError } from './errors.js';
import { FormulaProfile } from './profile.js';
import { findFunction } from './registry.js';
import { isCollection, scopeOf } from './types.js';
/**
 * Binds every reference to the host's stable symbol id (§4.3, §8): the AST that is stored refers to ids, and the
 * text is re-rendered from names on load. A reference that already carries an id (a stored AST) is resolved by
 * that id first, so a renamed symbol still binds; an unresolvable reference is FL-E02.
 *
 * A collection symbol opens a child scope (§4.7): when the first argument of a function that has a collection
 * form names one, the call's other arguments — the expression and `where:` — are resolved against the
 * collection's members, after the aggregate-body profile has accepted them (FL-E05 for a function call inside,
 * `round()` and nested aggregates included).
 */
export function resolve(root, symbols, errors) {
    return new Resolver(errors).resolve(root, symbols);
}
/** The symbol a reference names in `scope`: by the id it already carries, then by its path. */
export function lookupSymbol(node, scope) {
    return (node.symbol !== null ? scope.resolveId?.(node.symbol) : undefined) ?? scope.resolve(node.path);
}
class Resolver {
    constructor(errors) {
        this.errors = errors;
    }
    resolve(node, scope) {
        if (node.kind === 'ref')
            return this.bind(node, scope);
        if (node.kind === 'call')
            return this.resolveCall(node, scope);
        return mapChildren(node, (child) => this.resolve(child, scope));
    }
    bind(node, scope) {
        const symbol = lookupSymbol(node, scope);
        if (!symbol) {
            this.errors.push(formulaError(FormulaErrorCodes.UnknownSymbol, node.span, `'${pathText(node)}' is not a known symbol here.`, {
                path: pathText(node),
            }));
            return node;
        }
        return { ...node, symbol: symbol.id };
    }
    resolveCall(call, scope) {
        const collection = this.collectionArgument(call, scope);
        if (!collection)
            return mapChildren(call, (child) => this.resolve(child, scope));
        const first = call.args[0];
        const members = scopeOf(collection);
        const body = (node) => this.resolveBody(node, members);
        return {
            ...call,
            args: [{ ...first, symbol: collection.id }, ...call.args.slice(1).map(body)],
            named: call.named.map((n) => ({ name: n.name, value: body(n.value) })),
        };
    }
    /** The collection symbol the first argument names, when the function has a collection form and the argument is a reference to one. */
    collectionArgument(call, scope) {
        const first = call.args[0];
        if (!findFunction(call.fn)?.aggregate || first?.kind !== 'ref')
            return undefined;
        const symbol = lookupSymbol(first, scope);
        return symbol && isCollection(symbol.type) ? symbol : undefined;
    }
    /** An aggregate's expression or `where:`: the body profile first, then the collection's scope. */
    resolveBody(node, members) {
        const disallowed = FormulaProfile.aggregateBody.check(node, null);
        if (disallowed.length > 0) {
            this.errors.push(...disallowed);
            return node;
        }
        return this.resolve(node, members);
    }
}
